import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Calendar, User, ArrowRight, Plus, Pencil, Trash2, X, Upload, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { RichTextEditor } from '../components/ui/RichTextEditor';
import { createMedia, updateMedia, deleteMedia, uploadMediaFile, adminListMedia } from '../services/media';
import axios from 'axios';
import DOMPurify from 'dompurify';
import API_BASE_URL from '../services/api';

const API = `${API_BASE_URL}/api`;

const TYPES = [
  { value: 'blog_post', label: 'Article de blog' },
  { value: 'article', label: 'Article' },
  { value: 'tract', label: 'Tract' },
  { value: 'documentary', label: 'Documentaire' },
];
const EMPTY = { title: '', media_type: 'blog_post', category: '', image_url: '', excerpt: '', content: '', status: 'published' };

const Blog = () => {
  const { user, token } = useAuth();
  const [posts, setPosts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('Tous');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [readPost, setReadPost] = useState(null);

  const isFounder = user && user.user_type === 'fondateur';

  useEffect(() => { loadPosts(); /* eslint-disable-next-line */ }, [isFounder]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { setReadPost(null); setModalOpen(false); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const loadPosts = async () => {
    setLoading(true); setError('');
    try {
      const types = ['blog_post', 'article', 'tract', 'documentary'];
      const all = [];
      for (const t of types) {
        try {
          if (isFounder && token) {
            const data = await adminListMedia({ media_type: t, limit: 100 }, token);
            const arr = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
            all.push(...arr);
          } else {
            const res = await axios.get(`${API}/media/items`, { params: { media_type: t, limit: 50 } });
            const d = res?.data;
            const arr = Array.isArray(d) ? d : (Array.isArray(d?.items) ? d.items : []);
            all.push(...arr);
          }
        } catch (e) { /* ignore per-type */ }
      }
      const map = new Map();
      all.forEach(it => { if (it && it.id && !map.has(it.id)) map.set(it.id, it); });
      const combined = Array.from(map.values()).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setPosts(combined);
    } catch (e) {
      setError('Erreur lors du chargement des articles');
    } finally { setLoading(false); }
  };

  const categories = ['Tous', ...Array.from(new Set(posts.map(p => p.category).filter(Boolean)))];
  const filtered = selectedCategory === 'Tous' ? posts : posts.filter(p => p.category === selectedCategory);

  const openCreate = () => { setEditingId(null); setForm(EMPTY); setModalOpen(true); };
  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      title: p.title || '', media_type: p.media_type || 'blog_post', category: p.category || '',
      image_url: p.image_url || '', excerpt: p.excerpt || '', content: p.content || '',
      status: p.status || 'published',
    });
    setModalOpen(true);
  };

  const handleImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadMediaFile(file, token);
      setForm(f => ({ ...f, image_url: res.url }));
    } catch (err) {
      setError("Échec de l'upload de l'image");
    } finally { setUploading(false); }
  };

  const handleSave = async () => {
    if (!form.title.trim()) { setError('Le titre est obligatoire'); return; }
    setSaving(true); setError('');
    try {
      const payload = { ...form };
      if (editingId) await updateMedia(editingId, payload, token);
      else await createMedia(payload, token);
      setModalOpen(false); setForm(EMPTY); setEditingId(null);
      await loadPosts();
    } catch (err) {
      setError(err.response?.data?.detail || "Échec de l'enregistrement");
    } finally { setSaving(false); }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Supprimer « ${p.title} » ? Cette action est définitive.`)) return;
    try { await deleteMedia(p.id, token); await loadPosts(); }
    catch (err) { setError('Échec de la suppression'); }
  };

  const PostCardImage = ({ item }) => {
    const src = item.image_url || item.video_url || item.document_url || null;
    if (!src) return (
      <div className="aspect-video flex items-center justify-center bg-gradient-to-br from-primary/10 to-secondary/10">
        <span className="text-muted-foreground text-sm">Aperçu non disponible</span>
      </div>
    );
    return (
      <div className="aspect-video overflow-hidden">
        <img src={src} alt={item.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
      </div>
    );
  };

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-4 py-8">
        <div className="text-center mb-12">
          <div className="flex justify-between items-center mb-6">
            <div className="flex-1">
              <h1 className="text-4xl font-bold mb-4">Blog & Tracts</h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Suivez nos actualités, nos tracts et documentaires, et l'impact de nos actions sur le terrain.
              </p>
            </div>
            {isFounder && (
              <div className="ml-6">
                <Button onClick={openCreate} className="flex items-center gap-2" data-testid="blog-new-btn">
                  <Plus className="h-4 w-4" /> Nouvel Article
                </Button>
              </div>
            )}
          </div>
        </div>

        {error && <Alert variant="destructive" className="mb-6"><AlertDescription data-testid="blog-error">{error}</AlertDescription></Alert>}

        {categories.length > 1 && (
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {categories.map((cat) => (
              <Button key={cat} variant={selectedCategory === cat ? 'default' : 'outline'} onClick={() => setSelectedCategory(cat)} className="mb-2">{cat}</Button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Chargement des contenus...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <h3 className="text-2xl font-bold mb-4">Aucun contenu disponible</h3>
            <p className="text-muted-foreground mb-6">Revenez bientôt pour de nouveaux articles et tracts.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((item) => (
              <Card key={item.id} className="overflow-hidden hover:shadow-lg transition-shadow flex flex-col" data-testid="blog-card">
                <PostCardImage item={item} />
                <CardHeader>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {item.category && <Badge variant="outline">{item.category}</Badge>}
                      {item.status && item.status !== 'published' && <Badge variant="secondary">Brouillon</Badge>}
                    </div>
                    <div className="flex items-center text-sm text-muted-foreground">
                      <Calendar className="h-4 w-4 mr-1" />
                      {new Date(item.published_at || item.created_at).toLocaleDateString('fr-FR')}
                    </div>
                  </div>
                  <CardTitle className="line-clamp-2">{item.title}</CardTitle>
                  <CardDescription className="line-clamp-2">{item.excerpt || item.description}</CardDescription>
                </CardHeader>
                <CardContent className="mt-auto">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-sm text-muted-foreground"><User className="h-4 w-4 mr-1" /> PADAID</div>
                    <Button variant="ghost" onClick={() => setReadPost(item)} data-testid="blog-read-btn">Lire<ArrowRight className="h-4 w-4 ml-2" /></Button>
                  </div>
                  {isFounder && (
                    <div className="flex gap-2 mt-3 pt-3 border-t border-border">
                      <Button size="sm" variant="outline" className="flex-1" onClick={() => openEdit(item)} data-testid="blog-edit-btn"><Pencil className="h-3.5 w-3.5 mr-1" /> Modifier</Button>
                      <Button size="sm" variant="destructive" className="flex-1" onClick={() => handleDelete(item)} data-testid="blog-delete-btn"><Trash2 className="h-3.5 w-3.5 mr-1" /> Supprimer</Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Modal création / édition */}
      {modalOpen && (
        <div className="fixed inset-0 z-[100] flex items-start justify-center bg-black/60 backdrop-blur-sm overflow-y-auto p-4" data-testid="blog-editor-modal">
          <div className="bg-card border border-border rounded-2xl w-full max-w-2xl my-8 shadow-2xl">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className="text-lg font-bold">{editingId ? "Modifier l'article" : 'Nouvel article'}</h2>
              <button onClick={() => setModalOpen(false)} className="text-muted-foreground hover:text-foreground"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Titre *</label>
                <input className="w-full rounded-md border border-border bg-background px-3 py-2" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="blog-title-input" placeholder="Titre de l'article" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Type</label>
                  <select className="w-full rounded-md border border-border bg-background px-3 py-2" value={form.media_type} onChange={e => setForm({ ...form, media_type: e.target.value })} data-testid="blog-type-select">
                    {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Catégorie</label>
                  <input className="w-full rounded-md border border-border bg-background px-3 py-2" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} placeholder="ex: Actualités" data-testid="blog-category-input" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Image de couverture</label>
                <div className="flex items-center gap-3">
                  <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-border cursor-pointer hover:bg-primary/5 text-sm">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Choisir une image
                    <input type="file" accept="image/*" className="hidden" onChange={handleImage} data-testid="blog-image-input" />
                  </label>
                  {form.image_url && <img src={form.image_url} alt="cover" className="h-12 w-20 object-cover rounded" />}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Extrait (résumé court)</label>
                <textarea className="w-full rounded-md border border-border bg-background px-3 py-2" rows={2} value={form.excerpt} onChange={e => setForm({ ...form, excerpt: e.target.value })} data-testid="blog-excerpt-input" placeholder="Court résumé affiché sur la carte" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Contenu</label>
                <RichTextEditor key={editingId || 'new'} value={form.content} onChange={(html) => setForm(f => ({ ...f, content: html }))} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Statut</label>
                <select className="w-full rounded-md border border-border bg-background px-3 py-2" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} data-testid="blog-status-select">
                  <option value="published">Publié (visible par tous)</option>
                  <option value="draft">Brouillon (admin seulement)</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-border">
              <Button variant="ghost" onClick={() => setModalOpen(false)}>Annuler</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="blog-save-btn">{saving ? 'Enregistrement…' : 'Enregistrer'}</Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal lecture */}
      {readPost && (
        <div className="fixed inset-0 z-[100] flex items-start justify-center bg-black/60 backdrop-blur-sm overflow-y-auto p-4" data-testid="blog-read-modal" onClick={() => setReadPost(null)}>
          <div className="bg-card border border-border rounded-2xl w-full max-w-3xl my-8 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center gap-2">
                {readPost.category && <Badge variant="outline">{readPost.category}</Badge>}
                <span className="text-sm text-muted-foreground">{new Date(readPost.published_at || readPost.created_at).toLocaleDateString('fr-FR')}</span>
              </div>
              <button onClick={() => setReadPost(null)} className="text-muted-foreground hover:text-foreground"><X size={20} /></button>
            </div>
            {readPost.image_url && <img src={readPost.image_url} alt={readPost.title} className="w-full max-h-72 object-cover" />}
            <div className="p-6">
              <h1 className="text-2xl font-bold mb-4">{readPost.title}</h1>
              {readPost.content
                ? <div className="rte-content" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(readPost.content) }} />
                : <p className="text-muted-foreground">{readPost.excerpt || readPost.description || 'Aucun contenu.'}</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Blog;
